import { MdOutlineFilterAlt, MdOutlineSync } from 'react-icons/md';
import Button from '../Buttons/Button';
import Period from '../Inputs/Date/Period';
import CustomSelects, { ICustomSelects } from '../Inputs/Selects/CustomSelect';
import { useState, useEffect } from 'react';
import useIsMobile from '../../hooks/useIsMobile';

export interface ICategoryProps extends ICustomSelects {
    numberOfArticles: number | string;
    disabledCategories?: boolean;
}

interface IDateProps {
    onHandleChangePeriodFrom: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onHandleChangePeriodTo: (e: React.ChangeEvent<HTMLInputElement>) => void;
    disabledPeriodFrom: boolean;
    disabledPeriodTo: boolean;
    minPeriodDateFrom: string;
    maxPeriodDateFrom: string;
    minPeriodDateTo: string;
    maxPeriodDateTo: string;
    valueDateFrom: string;
    valueDateTo: string;
}

interface IFilters {
    dateProps: IDateProps;
    categoryProps: ICategoryProps;
    onClearFilters: () => void;
}

const Filters = ({ dateProps, categoryProps, onClearFilters }: IFilters) => {

    const {
        onHandleChangePeriodFrom,
        onHandleChangePeriodTo,
        disabledPeriodFrom,
        disabledPeriodTo,
        minPeriodDateFrom,
        maxPeriodDateFrom,
        minPeriodDateTo,
        maxPeriodDateTo,
        valueDateFrom,
        valueDateTo,
    } = dateProps;

    const {
        options,
        numberOfArticles,
        onSelect,
        selectedItem,
        defaultSelectedItem,
        label,
        disabledCategories = false,
        variant,
    } = categoryProps;

    const [visible, setVisible] = useState<boolean>(false);
    const isMobile = useIsMobile(1280);

    const showFilters = !isMobile || visible;

    const [isExpanded, setIsExpanded] = useState<boolean>(showFilters);

    useEffect(() => {
        if (!showFilters) {
            setIsExpanded(false);
        }
    }, [showFilters]);

    const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
        if (e.propertyName === 'grid-template-rows' && showFilters) {
            setIsExpanded(true);
        }
    };

    const hasActiveFilters = !!selectedItem || !!valueDateFrom || !!valueDateTo;

    return (
        <div className='mt-12 flex flex-col lg:flex-row justify-between'>
            <div className='flex flex-col gap-2 md:gap-0 lg:flex-row lg:justify-between items-start lg:items-center w-full'>
                <div className='flex justify-between items-center w-full lg:w-fit'>
                    <span className='text-blarge'>
                        Total de artículos:
                        <span className='font-medium'>
                            {numberOfArticles}
                        </span>
                    </span>
                    {isMobile && (
                        <Button
                            label=""
                            icon={MdOutlineFilterAlt}
                            iconSize={24}
                            showIconLeft={false}
                            showIconRight={true}
                            className="text-brand-green-30 rounded-sm !h-full !px-0 transition-[filter] duration-200 hover:brightness-110"
                            aria-label="Botón de expandir filtros"
                            onClick={() => setVisible(!visible)}
                        />
                    )}
                </div>
                <div
                    onTransitionEnd={handleTransitionEnd}
                    className={`lg:my-0 transition-all duration-500 ease-in-out w-full lg:w-fit grid ${showFilters ? 'mt-8 lg:mt-0 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                >
                    <div className={isExpanded ? '' : 'overflow-hidden'}>
                        <div className='flex flex-col md:flex-row justify-between sm:justify-start sm:items-end gap-2 sm:gap-6 w-full lg:w-fit'>
                            <CustomSelects
                                options={options}
                                onSelect={onSelect}
                                selectedItem={selectedItem}
                                defaultSelectedItem={defaultSelectedItem}
                                label={label}
                                disabled={disabledCategories}
                                variant={variant}
                                className='w-full md:w-fit md:min-w-[150px]'
                            />
                            <Period
                                labelPeriod='Desde'
                                onHandleChangePeriod={onHandleChangePeriodFrom}
                                disabledPeriod={disabledPeriodFrom}
                                minPeriodDate={minPeriodDateFrom}
                                maxPeriodDate={maxPeriodDateFrom}
                                value={valueDateFrom}
                            />
                            <Period
                                labelPeriod='Hasta'
                                onHandleChangePeriod={onHandleChangePeriodTo}
                                disabledPeriod={disabledPeriodTo}
                                minPeriodDate={minPeriodDateTo}
                                maxPeriodDate={maxPeriodDateTo}
                                value={valueDateTo}
                            />
                            <Button
                                label="Limpiar filtro"
                                onClick={onClearFilters}
                                className="text-primary-40 whitespace-nowrap !px-0"
                                aria-label="Botón de limpiar filtros"
                                showIconLeft={false}
                                showIconRight={true}
                                disabled={!hasActiveFilters}
                                icon={MdOutlineSync}
                                iconSize={18}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Filters